import { orgId } from './JsonLd';
import type { FaqItem } from '@/content/faq';

// ── Organization ─────────────────────────────────────────────────────────────

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'For Visibility',
    url: 'https://forvisibility.com',
    logo: 'https://forvisibility.com/icon-512.png',
    description:
      'AI-powered reseller platform — list once, sell everywhere, auto-delist when sold.',
    sameAs: [
      'https://github.com/kab0rn/flipper-os',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@forvisibility.com',
    },
  };
}

// ── WebSite ──────────────────────────────────────────────────────────────────

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'For Visibility',
    url: 'https://forvisibility.com',
    description:
      'AI command center for resellers — inventory, listings, pricing, and messaging with AI agents.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://forvisibility.com/inventory?search={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

// ── SoftwareApplication ──────────────────────────────────────────────────────

export function buildSoftwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'For Visibility',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '0',
      highPrice: '129',
      priceCurrency: 'USD',
      offerCount: '4',
      offers: [
        { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Pro', price: '19', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Power', price: '49', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Studio', price: '129', priceCurrency: 'USD' },
      ],
    },
    featureList: [
      'AI listing generation from photos or descriptions',
      'Auto-delist across platforms when sold',
      'MCP server — manage inventory from Claude, Cursor, or ChatGPT',
      '30-day undo on every agent action',
      'Schedule-C and 1099-K tax exports',
      'Co-Pilot mode: AI drafts, you approve',
    ],
  };
}

// ── Pricing (AggregateOffer) ─────────────────────────────────────────────────

/**
 * A pricing tier as consumed by {@link buildPricingSchema}. Kept deliberately
 * loose/structural so callers (e.g. src/data/pricingTiers.ts) can pass their
 * own richer tier objects without a hard type dependency. Only `name` is
 * required; `price` may be a number or a string ("0", "$19", "Free", …).
 */
export interface PricingTierLike {
  name: string;
  price?: number | string;
  priceCurrency?: string;
  [key: string]: unknown;
}

/** Strip currency symbols/commas and coerce to a finite number, or undefined. */
function parsePrice(price: number | string | undefined): number | undefined {
  if (typeof price === 'number') return Number.isFinite(price) ? price : undefined;
  if (typeof price !== 'string') return undefined;
  const cleaned = price.replace(/[^0-9.]/g, '');
  if (cleaned === '') return undefined;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Builds an AggregateOffer (wrapped in a SoftwareApplication) describing the
 * pricing tiers. Tolerant of partial/empty input: tiers without a parseable
 * price still emit an Offer (without a `price` field), and low/high/offerCount
 * are derived only from the prices that parse.
 */
export function buildPricingSchema(tiers: readonly PricingTierLike[]) {
  const offers = (tiers ?? []).map((tier) => {
    const numeric = parsePrice(tier.price);
    const offer: Record<string, unknown> = {
      '@type': 'Offer',
      name: tier.name,
      priceCurrency: tier.priceCurrency ?? 'USD',
    };
    if (numeric !== undefined) offer.price = String(numeric);
    return offer;
  });

  const prices = (tiers ?? [])
    .map((tier) => parsePrice(tier.price))
    .filter((n): n is number => n !== undefined);

  const aggregate: Record<string, unknown> = {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    offerCount: String(offers.length),
    offers,
  };
  if (prices.length > 0) {
    aggregate.lowPrice = String(Math.min(...prices));
    aggregate.highPrice = String(Math.max(...prices));
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'For Visibility',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: aggregate,
  };
}

// ── FAQPage ──────────────────────────────────────────────────────────────────

export function buildFAQPageSchema(faqs: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// ── BreadcrumbList (dynamic) ─────────────────────────────────────────────────

export function buildBreadcrumbListSchema(
  items: { name: string; path: string }[],
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: orgId(item.path),
    })),
  };
}

// ── ComparisonPage (for /compare/* pages) ────────────────────────────────────

export function buildComparisonPageSchema(title: string, description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `https://forvisibility.com${window.location.pathname}`,
    about: {
      '@type': 'Thing',
      name: title,
    },
  };
}
